import {
	Card,
	CardBody,
	CardHeader,
	CardHeading,
	FormattedDateTime,
	IconButton,
	Id,
	Loading,
	Text,
	TextLink,
	useDynamicConfig,
	useEditDrawer,
} from "@dgs/core";
import { useQuery } from "@tanstack/react-query";
import { FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import styled from "styled-components";
import { ClippingDetailsHeaderActions } from "../../clippings/details/ClippingDetailsHeaderActions";
import { FileUploadDrawer } from "../../clippings/details/FileUploadDrawer";
import { useClippings } from "../../clippings/useClippings";
import { clippingsService } from "../../shared/api/clippingsService";
import { getFullNameFromContactOption } from "../../shared/select/ContactSelectField";
import { ClippingUpdateDrawer } from "../ClippingDrawer";
import { DescriptionList } from "./DescriptionList";
import { RawProviderData } from "./RawProviderData";

const StyledTextLink = styled(TextLink)`
	display: inline-flex;
`;

const PreText = styled(Text)`
	white-space: pre-wrap;
`;

const Row = styled.div`
	display: flex;
	gap: ${({ theme }) => theme.spacing(4)};
	align-items: center;

	a {
		color: inherit;
	}
`;

export const ClippingDetails: FC = () => {
	const { clippingId = "" } = useParams();
	const { t } = useTranslation();
	const { apiUrl } = useDynamicConfig();
	const navigate = useNavigate();
	const drawerProps = useEditDrawer();
	const { updateClipping, deleteClipping, deleteFile } = useClippings();
	const [enableQuery, setEnableQuery] = useState<boolean>(true);
	const { data: clipping, isLoading } = useQuery({
		queryKey: clippingsService.showKey(clippingId),
		queryFn: () => clippingsService.show(clippingId).then((r) => r.data.data),
		enabled: enableQuery,
	});

	const handleDelete = useCallback(
		async (id: Id) => {
			setEnableQuery(false);
			const response = await deleteClipping(id);
			if (response) {
				navigate(-1);
			} else {
				setEnableQuery(true);
			}
		},
		[navigate, deleteClipping],
	);

	if (!clipping || isLoading) {
		return <Loading />;
	}

	return (
		<>
			<Card>
				<CardHeader>
					<CardHeading>{clipping.title}</CardHeading>
					<ClippingDetailsHeaderActions
						onEdit={() => drawerProps.handleOpen(clipping.id)}
						onDelete={() => handleDelete(clipping.id)}
					/>
				</CardHeader>
				<CardBody>
					<DescriptionList>
						<dt>{t("Content")}</dt>
						<dd>
							{!clipping.body && "-"}
							{clipping.body && <PreText>{clipping.body}</PreText>}
						</dd>
						<dt>{t("URL")}</dt>
						<dd>
							{!clipping.link && "-"}
							{clipping.link && (
								<StyledTextLink to={clipping.link} target="_blank">
									{clipping.link}
								</StyledTextLink>
							)}
						</dd>
						<dt>{t("Published at")}</dt>
						<dd>
							{!clipping.publishedAt && "-"}
							{clipping.publishedAt && <FormattedDateTime dateTime={clipping.publishedAt} />}
						</dd>
						<dt>{t("Event")}</dt>
						<dd>
							{!clipping.event && "-"}
							{clipping.event && (
								<StyledTextLink to={`/events/active/${clipping.event.id}`}>{clipping.event.name}</StyledTextLink>
							)}
						</dd>
						<dt>{t("Media equivalence value")}</dt>
						<dd>{clipping.mediaEquivalenceValue ?? "-"}</dd>
						<dt>{t("Reach")}</dt>
						<dd>{clipping.reach ?? "-"}</dd>
						<dt>{t("Contact")}</dt>
						<dd>
							{!clipping.contact && "-"}
							{clipping.contact && (
								<StyledTextLink to={`/contacts/active/${clipping.contact.id}`}>
									{getFullNameFromContactOption(clipping.contact)}
								</StyledTextLink>
							)}
						</dd>
						<dt>{t("Company")}</dt>
						<dd>
							{!clipping.company && "-"}
							{clipping.company && (
								<StyledTextLink to={`/companies/active/${clipping.company.id}`}>{clipping.company.name}</StyledTextLink>
							)}
						</dd>
						<dt>{t("File")}</dt>
						<dd>
							{clipping.file ? (
								<Row>
									<a href={`${apiUrl}/files/${clipping.file.id}/download`} target="_blank" rel="noreferrer">
										{clipping.file.filename}
									</a>
									<IconButton
										title={t("Delete file")}
										icon="delete"
										color="danger"
										size="small"
										onClick={() => deleteFile(clippingId)}
									/>
								</Row>
							) : (
								<FileUploadDrawer />
							)}
						</dd>
						<dt></dt>
						<dd>
							{clipping.provider !== "DGS" && clipping.rawProviderData && (
								<RawProviderData rawProviderData={clipping.rawProviderData} />
							)}
						</dd>
					</DescriptionList>
				</CardBody>
			</Card>
			{drawerProps.editEntityId && (
				<ClippingUpdateDrawer
					{...drawerProps}
					id={drawerProps.editEntityId}
					service={clippingsService}
					onSubmit={(formValues) => updateClipping(clipping.id, formValues)}
				/>
			)}
		</>
	);
};
